import DataTable from "../../components/common/DataTable";
import React from "react";
import { Badge } from "@mantine/core";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { Search, ArrowUp, ArrowDown } from "lucide-react";

const searchAnalytics = [
  { key: "1", keyword: "Action movies", searches: 12500, clicks: 8750, ctr: 70.0, conversions: 2100, trend: "up", popularity: "hot" },
  { key: "2", keyword: "Comedy series", searches: 10200, clicks: 6120, ctr: 60.0, conversions: 1836, trend: "up", popularity: "trending" },
  { key: "3", keyword: "Romance films", searches: 9850, clicks: 5910, ctr: 60.0, conversions: 1183, trend: "down", popularity: "normal" },
  { key: "4", keyword: "Horror movies", searches: 8920, clicks: 5352, ctr: 60.0, conversions: 1071, trend: "up", popularity: "trending" },
  { key: "5", keyword: "Sci-fi series", searches: 7650, clicks: 4590, ctr: 60.0, conversions: 918, trend: "down", popularity: "normal" },
];

const searchColumns = [
  { title: "Search Keyword", dataIndex: "keyword", key: "keyword" },
  { title: "Total Searches", dataIndex: "searches", key: "searches", render: (t) => t.toLocaleString() },
  { title: "Clicks", dataIndex: "clicks", key: "clicks", render: (t) => t.toLocaleString() },
  { title: "CTR", dataIndex: "ctr", key: "ctr", render: (t) => `${t.toFixed(1)}%` },
  { title: "Conversions", dataIndex: "conversions", key: "conversions", render: (t) => t.toLocaleString() },
  { title: "Trend", dataIndex: "trend", key: "trend", render: (t) => t === "up" ? <span className="flex items-center gap-1 text-green-500"><ArrowUp size={12} />Increasing</span> : <span className="flex items-center gap-1 text-red-500"><ArrowDown size={12} />Decreasing</span> },
  { title: "Popularity", dataIndex: "popularity", key: "popularity", render: (p) => <Badge color={p === "hot" ? "red" : p === "trending" ? "orange" : "blue"} variant="light" size="sm">{p.toUpperCase()}</Badge> },
];

const searchTrendData = [
  { day: "Mon", searches: 8900, conversions: 1425, zeroResults: 356 },
  { day: "Tue", searches: 9200, conversions: 1472, zeroResults: 368 },
  { day: "Wed", searches: 10100, conversions: 1616, zeroResults: 404 },
  { day: "Thu", searches: 9800, conversions: 1568, zeroResults: 392 },
  { day: "Fri", searches: 11200, conversions: 1792, zeroResults: 448 },
];

const popularSearchesData = [
  { month: "Jan", topSearches: 15000, newSearches: 2340 },
  { month: "Feb", topSearches: 16200, newSearches: 2520 },
  { month: "Mar", topSearches: 17850, newSearches: 2880 },
  { month: "Apr", topSearches: 18900, newSearches: 3125 },
];

const SearchAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Search}
      title="Search Analytics"
      subtitle="Search terms, keywords & user discovery patterns"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Searches" value={1568000} suffix="" />
        <StatCard title="Conversions" value={250240} suffix="" />
        <StatCard title="Zero Results" value={31240} suffix="" />
        <StatCard title="Avg CTR" value={65.2} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Daily Search Metrics">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={searchTrendData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="day" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="searches" fill="#3b82f6" name="Total Searches" />
                <Bar dataKey="conversions" fill="#10b981" name="Conversions" />
                <Bar dataKey="zeroResults" fill="#ef4444" name="Zero Results" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Popular vs New Searches">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={popularSearchesData}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="month" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="topSearches" stroke="#3b82f6" strokeWidth={2} name="Top Searches" />
                <Line type="monotone" dataKey="newSearches" stroke="#f59e0b" strokeWidth={2} name="New Searches" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Top Search Keywords">
        <DataTable dataSource={searchAnalytics} columns={searchColumns} pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default SearchAnalyticsPage;
